import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { purchaseAPI } from '../../api';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const AdminPurchases = () => {
  // All purchases state (Admin Orders Management)
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [adminOrderSearch, setAdminOrderSearch] = useState('');
  const [adminOrderStatus, setAdminOrderStatus] = useState('ALL');

  // --- Monthly Leg Business Tracker State (Power Leg vs Rest of Leg) ---
  const [legMonth, setLegMonth] = useState(() => {
    const istOffset = 5.5 * 60 * 60 * 1000;
    return new Date(Date.now() + istOffset).getUTCMonth() + 1;
  });
  const [legYear, setLegYear] = useState(() => {
    const istOffset = 5.5 * 60 * 60 * 1000;
    return new Date(Date.now() + istOffset).getUTCFullYear();
  });
  const [filterLeg, setFilterLeg] = useState('all'); // 'all', 'power', 'rest', 'self'
  const [legReportData, setLegReportData] = useState(null);
  const [legReportLoading, setLegReportLoading] = useState(true);
  const [legReportError, setLegReportError] = useState('');
  const [dayViewFilter, setDayViewFilter] = useState('activeOnly'); // 'activeOnly' | 'all'
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedDays, setExpandedDays] = useState(new Set());
  const [selectedChartDay, setSelectedChartDay] = useState(null);
  const [showAllLegsBreakdown, setShowAllLegsBreakdown] = useState(false);

  // Admin Member / Network Inspector state
  const [targetSponsorInput, setTargetSponsorInput] = useState('');
  const [activeInspectedCode, setActiveInspectedCode] = useState('');

  // Section view tabs: 'all' | 'legTracker' | 'allPurchases'
  const [activeSectionView, setActiveSectionView] = useState('all');

  const availableYears = useMemo(() => {
    const currentYr = new Date().getFullYear();
    const years = [];
    for (let y = Math.min(2024, currentYr - 1); y <= currentYr + 1; y++) {
      years.push(y);
    }
    return years;
  }, []);

  // Fetch all purchases for Admin management table
  const fetchPurchases = async () => {
    try {
      setLoading(true);
      const response = await purchaseAPI.getAllPurchases();
      setPurchases(response.data || []);
    } catch (error) {
      console.error('Error fetching purchases:', error);
      setPurchases([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch monthly date-wise leg report (Power Leg vs Rest of Leg)
  const fetchMonthlyLegReport = useCallback(async () => {
    try {
      setLegReportLoading(true);
      setLegReportError('');

      const params = {
        year: legYear,
        month: legMonth,
        leg: filterLeg
      };
      if (activeInspectedCode.trim()) {
        params.sponsorCode = activeInspectedCode.trim();
      }

      const res = await purchaseAPI.getMonthlyLegReport(params);

      if (res.data?.success) {
        setLegReportData(res.data.data);
      } else {
        setLegReportError(res.data?.error || 'Failed to load monthly leg report');
      }
    } catch (err) {
      console.error('Fetch monthly leg report error:', err);
      setLegReportError(err.response?.data?.error || 'Failed to load leg purchase data');
    } finally {
      setLegReportLoading(false);
    }
  }, [legYear, legMonth, filterLeg, activeInspectedCode]);

  useEffect(() => {
    fetchPurchases();
  }, []);

  useEffect(() => {
    fetchMonthlyLegReport();
  }, [fetchMonthlyLegReport]);

  // Month navigation handlers
  const handlePrevMonth = () => {
    setSelectedChartDay(null);
    if (legMonth === 1) {
      setLegMonth(12);
      setLegYear(prev => prev - 1);
    } else {
      setLegMonth(prev => prev - 1);
    }
  };

  const handleNextMonth = () => {
    setSelectedChartDay(null);
    if (legMonth === 12) {
      setLegMonth(1);
      setLegYear(prev => prev + 1);
    } else {
      setLegMonth(prev => prev + 1);
    }
  };

  const handleCurrentMonth = () => {
    setSelectedChartDay(null);
    const istOffset = 5.5 * 60 * 60 * 1000;
    const nowIST = new Date(Date.now() + istOffset);
    setLegMonth(nowIST.getUTCMonth() + 1);
    setLegYear(nowIST.getUTCFullYear());
  };

  const handleSearchMemberSubmit = (e) => {
    e.preventDefault();
    if (targetSponsorInput.trim()) {
      setActiveInspectedCode(targetSponsorInput.trim());
      setSelectedChartDay(null);
    }
  };

  const handleResetToNetworkHead = () => {
    setTargetSponsorInput('');
    setActiveInspectedCode('');
    setSelectedChartDay(null);
  };

  const toggleDayExpansion = (dayNum) => {
    setExpandedDays(prev => {
      const next = new Set(prev);
      if (next.has(dayNum)) next.delete(dayNum);
      else next.add(dayNum);
      return next;
    });
  };

  const toggleAllExpanded = (daysToToggle) => {
    const allExpanded = daysToToggle.every(d => expandedDays.has(d.day));
    if (allExpanded) {
      setExpandedDays(new Set());
    } else {
      setExpandedDays(new Set(daysToToggle.map(d => d.day)));
    }
  };

  // Filtered days list for the leg table
  const filteredDays = useMemo(() => {
    if (!legReportData?.days) return [];
    let list = legReportData.days;

    if (selectedChartDay !== null) {
      list = list.filter(d => d.day === selectedChartDay);
    } else if (dayViewFilter === 'activeOnly') {
      list = list.filter(d => d.totalAmount > 0 || d.ordersCount > 0);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter(d => {
        const dateMatch = d.date.includes(q) || d.dayName.toLowerCase().includes(q) || String(d.day) === q;
        const orderMatch = (d.orders || []).some(o =>
          (o.buyerName && o.buyerName.toLowerCase().includes(q)) ||
          (o.buyerSponsorCode && o.buyerSponsorCode.toLowerCase().includes(q)) ||
          (o.orderNumber && o.orderNumber.toLowerCase().includes(q)) ||
          (o.legType && o.legType.toLowerCase().includes(q)) ||
          (o.legLeaderName && o.legLeaderName.toLowerCase().includes(q))
        );
        return dateMatch || orderMatch;
      });
    }

    return list;
  }, [legReportData, dayViewFilter, selectedChartDay, searchQuery]);

  const maxDayAmount = useMemo(() => {
    if (!legReportData?.days || legReportData.days.length === 0) return 1;
    const max = Math.max(...legReportData.days.map(d => d.totalAmount));
    return max > 0 ? max : 1;
  }, [legReportData]);

  // Admin order actions
  const handleAdminConfirmPurchase = async (purchaseId) => {
    try {
      setActionLoading(purchaseId);
      const response = await purchaseAPI.adminConfirmPurchase(purchaseId);

      setPurchases(prevPurchases =>
        prevPurchases.map(purchase =>
          purchase._id === purchaseId
            ? { ...purchase, status: 'CONFIRMED', updatedAt: new Date() }
            : purchase
        )
      );

      alert(response.data.message || 'Purchase confirmed successfully! Commission has been distributed.');
      fetchMonthlyLegReport();
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to confirm purchase';
      alert(errorMessage);
      console.error('Admin confirm purchase error:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleAdminCancelPurchase = async (purchaseId) => {
    if (!window.confirm('Are you sure you want to cancel this order? This action cannot be undone.')) {
      return;
    }

    try {
      setActionLoading(purchaseId);
      const response = await purchaseAPI.adminCancelPurchase(purchaseId);

      setPurchases(prevPurchases =>
        prevPurchases.map(purchase =>
          purchase._id === purchaseId
            ? { ...purchase, status: 'CANCELLED', updatedAt: new Date() }
            : purchase
        )
      );

      alert(response.data.message || 'Purchase cancelled successfully.');
      fetchMonthlyLegReport();
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to cancel purchase';
      alert(errorMessage);
      console.error('Admin cancel purchase error:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'CONFIRMED':
        return 'bg-green-100 text-green-700';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-700';
      case 'CANCELLED':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  // Filtered all purchases for Admin Table
  const filteredAllPurchases = useMemo(() => {
    return purchases.filter(p => {
      const matchesStatus = adminOrderStatus === 'ALL' || p.status === adminOrderStatus;
      if (!adminOrderSearch.trim()) return matchesStatus;
      const q = adminOrderSearch.toLowerCase().trim();
      const matchText =
        (p.orderNumber && p.orderNumber.toLowerCase().includes(q)) ||
        (p.userId?.name && p.userId.name.toLowerCase().includes(q)) ||
        (p.userId?.email && p.userId.email.toLowerCase().includes(q)) ||
        (p.userId?.mySponsorCode && p.userId.mySponsorCode.toLowerCase().includes(q)) ||
        (p.paymentMethod && p.paymentMethod.toLowerCase().includes(q));
      return matchesStatus && matchText;
    });
  }, [purchases, adminOrderStatus, adminOrderSearch]);

  return (
    <div className="space-y-8 max-w-7xl mx-auto p-4 md:p-6 bg-[#F8FAFC]">
      {/* Page Title & View Switcher */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#0F2F3D]">Purchases & Leg Business Tracker</h1>
          <p className="text-sm text-gray-600">
            Track monthly purchases date-wise with Power Leg and Rest of the Leg separate breakdown & manage orders
          </p>
        </div>

        {/* Section View Tabs */}
        <div className="flex items-center gap-1.5 bg-white p-1.5 rounded-xl shadow-sm border border-gray-200">
          <button
            onClick={() => setActiveSectionView('all')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
              activeSectionView === 'all'
                ? 'bg-[#0F2F3D] text-white shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            All Sections
          </button>
          <button
            onClick={() => setActiveSectionView('legTracker')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
              activeSectionView === 'legTracker'
                ? 'bg-[#E4572E] text-white shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            ⚡ Power Leg Tracker
          </button>
          <button
            onClick={() => setActiveSectionView('allPurchases')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
              activeSectionView === 'allPurchases'
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            📦 All Customer Orders ({purchases.length})
          </button>
        </div>
      </div>

      {/* ======================================================== */}
      {/* ⚡ MONTHLY POWER LEG VS REST OF LEG TRACKER SECTION       */}
      {/* ======================================================== */}
      {(activeSectionView === 'all' || activeSectionView === 'legTracker') && (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          {/* Tracker Header & Controls Bar */}
          <div className="p-5 md:p-6 bg-gradient-to-r from-[#0F2F3D] to-[#1A3D4D] text-white">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-[#E4572E] text-white">
                    Admin Leg Business Intelligence
                  </span>
                  <span className="text-xs text-gray-300">
                    Power Leg vs Rest of the Leg Date-Wise
                  </span>
                  {legReportData?.user && (
                    <span className="text-xs bg-white/20 text-white px-2.5 py-0.5 rounded-full font-mono">
                      Inspecting: {legReportData.user.name} ({legReportData.user.mySponsorCode})
                    </span>
                  )}
                </div>
                <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                  <span>📊</span>
                  <span>{MONTH_NAMES[legMonth - 1]} {legYear} Purchases Report</span>
                </h2>
              </div>

              {/* Month / Year Selectors */}
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={handlePrevMonth}
                  title="Previous Month"
                  className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white rounded-lg text-sm font-medium transition backdrop-blur-sm"
                >
                  ← Prev
                </button>

                <select
                  value={legMonth}
                  onChange={(e) => {
                    setSelectedChartDay(null);
                    setLegMonth(Number(e.target.value));
                  }}
                  className="bg-white text-gray-800 font-semibold px-3 py-1.5 rounded-lg text-sm border-none shadow-sm focus:ring-2 focus:ring-[#E4572E]"
                >
                  {MONTH_NAMES.map((name, idx) => (
                    <option key={idx + 1} value={idx + 1}>
                      {name}
                    </option>
                  ))}
                </select>

                <select
                  value={legYear}
                  onChange={(e) => {
                    setSelectedChartDay(null);
                    setLegYear(Number(e.target.value));
                  }}
                  className="bg-white text-gray-800 font-semibold px-3 py-1.5 rounded-lg text-sm border-none shadow-sm focus:ring-2 focus:ring-[#E4572E]"
                >
                  {availableYears.map((yr) => (
                    <option key={yr} value={yr}>
                      {yr}
                    </option>
                  ))}
                </select>

                <button
                  onClick={handleNextMonth}
                  title="Next Month"
                  className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white rounded-lg text-sm font-medium transition backdrop-blur-sm"
                >
                  Next →
                </button>

                <button
                  onClick={handleCurrentMonth}
                  title="Jump to Current Month"
                  className="px-3 py-1.5 bg-[#E4572E] hover:bg-[#d04a23] text-white rounded-lg text-sm font-semibold shadow-sm transition"
                >
                  This Month
                </button>
              </div>
            </div>

            {/* Admin Member Search Bar */}
            <div className="mt-4 pt-3 border-t border-white/10 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <form onSubmit={handleSearchMemberSubmit} className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-gray-300 font-medium">Inspect Member Network:</span>
                <input
                  type="text"
                  placeholder="Enter Sponsor Code (e.g. DMC88786)..."
                  value={targetSponsorInput}
                  onChange={(e) => setTargetSponsorInput(e.target.value)}
                  className="px-3 py-1 bg-white/15 border border-white/25 text-white placeholder-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-[#E4572E] w-64"
                />
                <button
                  type="submit"
                  className="px-3 py-1 bg-[#E4572E] hover:bg-[#d04a23] text-white text-xs font-semibold rounded-md transition"
                >
                  Inspect
                </button>
                {activeInspectedCode && (
                  <button
                    type="button"
                    onClick={handleResetToNetworkHead}
                    className="px-2.5 py-1 bg-white/20 hover:bg-white/30 text-white text-xs rounded-md transition"
                    title="Reset to Top Network Tree"
                  >
                    ✕ Reset to Top Network
                  </button>
                )}
              </form>

              <button
                onClick={fetchMonthlyLegReport}
                className="text-xs text-blue-200 hover:text-white flex items-center gap-1 self-start sm:self-auto"
              >
                <span>🔄</span> Refresh Report Data
              </button>
            </div>

            {/* Scope Filter Tabs */}
            <div className="mt-3 pt-3 border-t border-white/15 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-xs text-gray-300 font-medium mr-1">Leg Filter:</span>
                {[
                  { id: 'all', label: 'All Purchases' },
                  { id: 'power', label: '⚡ Power Leg Only' },
                  { id: 'rest', label: '🌱 Rest of the Leg Only' },
                  { id: 'self', label: '👤 Self Purchases Only' }
                ].map((f) => (
                  <button
                    key={f.id}
                    onClick={() => {
                      setFilterLeg(f.id);
                      setSelectedChartDay(null);
                    }}
                    className={`px-3 py-1 rounded-md text-xs font-semibold transition ${
                      filterLeg === f.id
                        ? 'bg-white text-[#0F2F3D] shadow-sm'
                        : 'bg-white/10 text-white hover:bg-white/20'
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>

              {/* Day View Filter */}
              <div className="flex items-center gap-2 text-xs">
                <span className="text-gray-300 font-medium">Show:</span>
                <button
                  onClick={() => setDayViewFilter('activeOnly')}
                  className={`px-2.5 py-1 rounded text-xs font-medium transition ${
                    dayViewFilter === 'activeOnly'
                      ? 'bg-[#E4572E] text-white'
                      : 'bg-white/10 text-gray-200 hover:bg-white/20'
                  }`}
                >
                  Active Dates Only
                </button>
                <button
                  onClick={() => setDayViewFilter('all')}
                  className={`px-2.5 py-1 rounded text-xs font-medium transition ${
                    dayViewFilter === 'all'
                      ? 'bg-[#E4572E] text-white'
                      : 'bg-white/10 text-gray-200 hover:bg-white/20'
                  }`}
                >
                  All Dates (1-{legReportData?.daysInMonth || 30})
                </button>
              </div>
            </div>
          </div>

          {/* Tracker Body */}
          <div className="p-5 md:p-6 bg-gray-50/50 space-y-6">
            {legReportLoading ? (
              <div className="flex flex-col items-center justify-center py-16">
                <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#E4572E] border-t-transparent mb-3"></div>
                <p className="text-sm text-gray-500 font-medium">
                  Calculating Power Leg vs Rest of the Leg purchase data...
                </p>
              </div>
            ) : legReportError ? (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
                <p className="text-red-600 font-medium text-sm mb-2">⚠️ {legReportError}</p>
                <button
                  onClick={fetchMonthlyLegReport}
                  className="px-3 py-1.5 bg-red-600 text-white text-xs font-medium rounded-md hover:bg-red-700"
                >
                  Retry
                </button>
              </div>
            ) : legReportData ? (
              <>
                {/* KPI Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                  {/* Total Purchases */}
                  <div className="bg-gradient-to-br from-blue-600 to-blue-700 text-white rounded-xl p-3.5 shadow-sm">
                    <div className="text-[11px] uppercase tracking-wider text-blue-200 font-medium">Total Purchases</div>
                    <div className="text-sm md:text-base font-bold mt-0.5">
                      ₹{(legReportData.summary.totalMonthlyPurchases || 0).toLocaleString()}
                    </div>
                    <div className="text-[10px] text-blue-100 mt-1 flex items-center gap-1">
                      <span>📦 {legReportData.summary.totalOrdersCount} Orders</span>
                    </div>
                  </div>

                  {/* Power Leg */}
                  <div className="bg-gradient-to-br from-amber-500 to-amber-600 text-white rounded-xl p-3.5 shadow-sm relative overflow-hidden">
                    <div className="text-[11px] uppercase tracking-wider text-amber-100 font-medium flex items-center gap-1">
                      <span>⚡ Power Leg</span>
                    </div>
                    <div className="text-sm md:text-base font-bold mt-0.5">
                      ₹{(legReportData.summary.powerLegMonthlyTotal || 0).toLocaleString()}
                    </div>
                    <div className="text-[10px] text-amber-100 mt-1 truncate">
                      {legReportData.summary.powerLeg ? (
                        <span>{legReportData.summary.powerLeg.name} ({legReportData.summary.powerLeg.mySponsorCode})</span>
                      ) : (
                        'No Power Leg'
                      )}
                    </div>
                  </div>

                  {/* Rest of the Leg */}
                  <div className="bg-gradient-to-br from-teal-600 to-teal-700 text-white rounded-xl p-3.5 shadow-sm">
                    <div className="text-[11px] uppercase tracking-wider text-teal-200 font-medium flex items-center gap-1">
                      <span>🌱 Rest of the Leg</span>
                    </div>
                    <div className="text-sm md:text-base font-bold mt-0.5">
                      ₹{(legReportData.summary.restOfLegMonthlyTotal || 0).toLocaleString()}
                    </div>
                    <div className="text-[10px] text-teal-100 mt-1">
                      Other Direct Branches
                    </div>
                  </div>

                  {/* Self Purchases */}
                  <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 text-white rounded-xl p-3.5 shadow-sm">
                    <div className="text-[11px] uppercase tracking-wider text-emerald-200 font-medium">Self Purchases</div>
                    <div className="text-sm md:text-base font-bold mt-0.5">
                      ₹{(legReportData.summary.selfMonthlyTotal || 0).toLocaleString()}
                    </div>
                    <div className="text-[10px] text-emerald-100 mt-1">
                      Personal Orders
                    </div>
                  </div>

                  {/* Power vs Rest Leg Ratio */}
                  <div className="bg-gradient-to-br from-indigo-600 to-indigo-700 text-white rounded-xl p-3.5 shadow-sm">
                    <div className="text-[11px] uppercase tracking-wider text-indigo-200 font-medium">Leg Ratio</div>
                    <div className="text-xs md:text-sm font-bold mt-0.5">
                      {legReportData.summary.powerRatio}% : {legReportData.summary.restRatio}%
                    </div>
                    <div className="text-[10px] text-indigo-100 mt-1 truncate">
                      Power : Rest of Leg
                    </div>
                  </div>

                  {/* Peak Date */}
                  <div className="bg-gradient-to-br from-slate-700 to-[#0F2F3D] text-white rounded-xl p-3.5 shadow-sm">
                    <div className="text-[11px] uppercase tracking-wider text-gray-300 font-medium">Peak Date</div>
                    <div className="text-xs md:text-sm font-bold mt-0.5 truncate">
                      {legReportData.summary.highestDate?.amount > 0 ? (
                        `Day ${legReportData.summary.highestDate.day}: ₹${legReportData.summary.highestDate.amount.toLocaleString()}`
                      ) : (
                        'No purchases'
                      )}
                    </div>
                    <div className="text-[10px] text-gray-300 mt-1 truncate">
                      Avg ₹{(legReportData.summary.averageDailyAmount || 0).toLocaleString()}/day
                    </div>
                  </div>
                </div>

                {/* Direct Legs Hierarchy Breakdown */}
                {legReportData.summary.allLegs && legReportData.summary.allLegs.length > 0 && (
                  <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm text-gray-800">
                          Direct Legs Contribution Breakdown ({legReportData.summary.allLegs.length} Direct Branches)
                        </span>
                        {legReportData.summary.powerLeg && (
                          <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-amber-100 text-amber-800 border border-amber-300">
                            Power Leg: {legReportData.summary.powerLeg.name} (₹{legReportData.summary.powerLeg.monthlyTotal.toLocaleString()})
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => setShowAllLegsBreakdown(prev => !prev)}
                        className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                      >
                        <span>{showAllLegsBreakdown ? 'Hide Legs Details' : 'View All Legs'}</span>
                        <span className="text-[10px]">{showAllLegsBreakdown ? '▲' : '▼'}</span>
                      </button>
                    </div>

                    {showAllLegsBreakdown && (
                      <div className="mt-4 pt-3 border-t border-gray-200 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                        {legReportData.summary.allLegs.map((leg) => (
                          <div
                            key={leg.mySponsorCode}
                            className={`rounded-lg p-3 border ${
                              leg.isPowerLeg
                                ? 'bg-amber-50/70 border-amber-300 ring-1 ring-amber-400'
                                : 'bg-gray-50/70 border-gray-200'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs font-bold text-gray-800 truncate">{leg.name}</span>
                              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                                leg.isPowerLeg ? 'bg-amber-500 text-white' : 'bg-gray-200 text-gray-700'
                              }`}>
                                {leg.isPowerLeg ? '⚡ Power Leg' : `Leg #${leg.rank}`}
                              </span>
                            </div>
                            <div className="text-[11px] text-gray-500 font-mono">{leg.mySponsorCode}</div>
                            <div className="mt-2 flex items-baseline justify-between">
                              <span className="text-xs font-semibold text-gray-900">
                                ₹{leg.monthlyTotal.toLocaleString()}
                              </span>
                              <span className="text-[10px] text-gray-500">
                                {leg.monthlyOrdersCount} orders • {leg.totalTeamCount} members
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Visual Daily Comparative Bar Chart */}
                <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                    <div>
                      <h3 className="text-sm font-bold text-gray-800 flex items-center gap-1.5">
                        <span>📈</span> Date-Wise Purchase Volume (Power Leg vs Rest of the Leg)
                      </h3>
                      <p className="text-xs text-gray-500">
                        Click on any date to inspect specific orders and purchases on that day
                      </p>
                    </div>
                    {selectedChartDay !== null && (
                      <button
                        onClick={() => setSelectedChartDay(null)}
                        className="inline-flex items-center gap-1 text-xs px-2.5 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md font-medium transition"
                      >
                        <span>✕</span> Clear Filter (Day {selectedChartDay})
                      </button>
                    )}
                  </div>

                  <div className="overflow-x-auto pb-2">
                    <div className="min-w-[680px] flex items-end gap-1.5 h-36 pt-4 border-b border-gray-200">
                      {legReportData.days.map((d) => {
                        const totalDay = d.totalAmount;
                        const heightPercent = totalDay > 0
                          ? Math.max(Math.round((totalDay / maxDayAmount) * 100), 12)
                          : 6;
                        const isSelected = selectedChartDay === d.day;
                        const hasBusiness = totalDay > 0;

                        const powerPct = totalDay > 0 ? (d.powerLegAmount / totalDay) * 100 : 0;
                        const restPct = totalDay > 0 ? (d.restOfLegAmount / totalDay) * 100 : 0;
                        const selfPct = totalDay > 0 ? (d.selfAmount / totalDay) * 100 : 0;

                        return (
                          <div
                            key={d.day}
                            onClick={() => setSelectedChartDay(prev => prev === d.day ? null : d.day)}
                            className="flex-1 flex flex-col items-center justify-end cursor-pointer group relative transition-all"
                          >
                            {/* Hover tooltip */}
                            <div className="opacity-0 group-hover:opacity-100 pointer-events-none absolute -top-16 z-20 bg-gray-900 text-white text-[10px] rounded px-2.5 py-1.5 whitespace-nowrap shadow-xl transition-opacity">
                              <div className="font-semibold">{d.date} ({d.dayName})</div>
                              <div>Total: ₹{d.totalAmount.toLocaleString()} ({d.ordersCount} orders)</div>
                              <div className="text-amber-300">⚡ Power Leg: ₹{d.powerLegAmount.toLocaleString()}</div>
                              <div className="text-teal-300">🌱 Rest of Leg: ₹{d.restOfLegAmount.toLocaleString()}</div>
                              {d.selfAmount > 0 && <div className="text-emerald-300">👤 Self: ₹{d.selfAmount.toLocaleString()}</div>}
                            </div>

                            {/* Stacked bar representing Power Leg & Rest of Leg */}
                            <div
                              style={{ height: `${heightPercent}%` }}
                              className={`w-full rounded-t-sm flex flex-col-reverse overflow-hidden transition-all duration-200 ${
                                isSelected
                                  ? 'ring-2 ring-offset-1 ring-[#E4572E]'
                                  : ''
                              }`}
                            >
                              {hasBusiness ? (
                                <>
                                  {powerPct > 0 && (
                                    <div
                                      style={{ height: `${powerPct}%` }}
                                      className="w-full bg-amber-500 group-hover:bg-amber-400"
                                      title={`Power Leg: ₹${d.powerLegAmount.toLocaleString()}`}
                                    />
                                  )}
                                  {restPct > 0 && (
                                    <div
                                      style={{ height: `${restPct}%` }}
                                      className="w-full bg-teal-500 group-hover:bg-teal-400"
                                      title={`Rest of Leg: ₹${d.restOfLegAmount.toLocaleString()}`}
                                    />
                                  )}
                                  {selfPct > 0 && (
                                    <div
                                      style={{ height: `${selfPct}%` }}
                                      className="w-full bg-emerald-600 group-hover:bg-emerald-500"
                                      title={`Self: ₹${d.selfAmount.toLocaleString()}`}
                                    />
                                  )}
                                </>
                              ) : (
                                <div className="w-full h-full bg-gray-200 group-hover:bg-gray-300" />
                              )}
                            </div>

                            <div className={`text-[10px] mt-1.5 font-medium ${
                              isSelected ? 'text-[#E4572E] font-bold' : hasBusiness ? 'text-gray-800' : 'text-gray-400'
                            }`}>
                              {d.day}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Chart Legend */}
                  <div className="flex flex-wrap items-center justify-between text-[11px] text-gray-500 mt-2 px-1 gap-2">
                    <div className="flex items-center gap-3">
                      <span className="flex items-center gap-1">
                        <span className="w-2.5 h-2.5 rounded-sm bg-amber-500"></span> ⚡ Power Leg
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="w-2.5 h-2.5 rounded-sm bg-teal-500"></span> 🌱 Rest of the Leg
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="w-2.5 h-2.5 rounded-sm bg-emerald-600"></span> 👤 Self Purchase
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="w-2.5 h-2.5 rounded-sm bg-gray-200"></span> Zero Activity
                      </span>
                    </div>
                    <div>
                      Highest Day Volume: <span className="font-semibold text-gray-800">₹{maxDayAmount.toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                {/* Date-Wise Purchases Table */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                  {/* Table Control Header */}
                  <div className="p-4 bg-gray-50/70 border-b border-gray-200 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <h3 className="text-sm md:text-base font-bold text-gray-900">
                        Date-Wise Purchases Breakdown ({filteredDays.length} {filteredDays.length === 1 ? 'Date' : 'Dates'})
                      </h3>
                      {selectedChartDay !== null && (
                        <span className="px-2 py-0.5 rounded text-xs font-semibold bg-orange-100 text-orange-800">
                          Filtered: Day {selectedChartDay}
                        </span>
                      )}
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      <div className="relative">
                        <input
                          type="text"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          placeholder="Search buyer, order, date..."
                          className="w-52 md:w-60 pl-8 pr-3 py-1.5 text-xs bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E4572E] focus:outline-none"
                        />
                        <span className="absolute left-2.5 top-2 text-gray-400 text-xs">🔍</span>
                        {searchQuery && (
                          <button
                            onClick={() => setSearchQuery('')}
                            className="absolute right-2.5 top-1.5 text-gray-400 hover:text-gray-600 text-xs"
                          >
                            ✕
                          </button>
                        )}
                      </div>

                      {filteredDays.length > 0 && (
                        <button
                          onClick={() => toggleAllExpanded(filteredDays)}
                          className="px-3 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-xs font-medium transition"
                        >
                          {filteredDays.every(d => expandedDays.has(d.day)) ? 'Collapse All' : 'Expand All'}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Table */}
                  {filteredDays.length === 0 ? (
                    <div className="text-center py-12 px-4">
                      <div className="text-4xl mb-2">📭</div>
                      <div className="font-semibold text-gray-700 text-sm">No purchases recorded for this selection</div>
                      <p className="text-xs text-gray-500 mt-1">
                        {searchQuery
                          ? 'Try clearing your search query.'
                          : dayViewFilter === 'activeOnly'
                          ? 'No purchases on active days. Switch to "All Dates" to see the full calendar.'
                          : 'No purchase records found for this month.'}
                      </p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-left text-xs md:text-sm">
                        <thead className="bg-gray-50 text-gray-700 text-xs uppercase tracking-wider font-semibold border-b border-gray-200">
                          <tr>
                            <th className="px-4 py-3">Date / Day</th>
                            <th className="px-4 py-3">Total Purchase</th>
                            <th className="px-4 py-3">⚡ Power Leg</th>
                            <th className="px-4 py-3">🌱 Rest of the Leg</th>
                            <th className="px-4 py-3">👤 Self Purchase</th>
                            <th className="px-4 py-3 text-center">Orders</th>
                            <th className="px-4 py-3 text-right">Details</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {filteredDays.map((dayItem) => {
                            const isExpanded = expandedDays.has(dayItem.day);
                            const hasOrders = dayItem.ordersCount > 0;

                            return (
                              <React.Fragment key={dayItem.day}>
                                <tr className={`transition-colors ${
                                  hasOrders ? 'hover:bg-orange-50/40' : 'bg-gray-50/30 text-gray-400 hover:bg-gray-100/50'
                                }`}>
                                  <td className="px-4 py-3 font-medium">
                                    <div className="flex items-center gap-2">
                                      <span className="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center font-bold text-gray-800 text-xs">
                                        {dayItem.day}
                                      </span>
                                      <div>
                                        <div className="text-gray-900 font-semibold text-xs">{dayItem.date}</div>
                                        <div className="text-[11px] text-gray-500">{dayItem.dayName}</div>
                                      </div>
                                    </div>
                                  </td>

                                  <td className="px-4 py-3 text-xs">
                                    <span className={`text-xs font-semibold ${dayItem.totalAmount > 0 ? 'text-[#0F2F3D]' : 'text-gray-400'}`}>
                                      ₹{dayItem.totalAmount.toLocaleString()}
                                    </span>
                                  </td>

                                  <td className="px-4 py-3 text-xs">
                                    <span className={dayItem.powerLegAmount > 0 ? 'text-amber-600 font-semibold text-xs' : 'text-gray-400 text-xs'}>
                                      ₹{dayItem.powerLegAmount.toLocaleString()}
                                    </span>
                                  </td>

                                  <td className="px-4 py-3 text-xs">
                                    <span className={dayItem.restOfLegAmount > 0 ? 'text-teal-600 font-semibold text-xs' : 'text-gray-400 text-xs'}>
                                      ₹{dayItem.restOfLegAmount.toLocaleString()}
                                    </span>
                                  </td>

                                  <td className="px-4 py-3 text-xs">
                                    <span className={dayItem.selfAmount > 0 ? 'text-emerald-600 font-medium text-xs' : 'text-gray-400 text-xs'}>
                                      ₹{dayItem.selfAmount.toLocaleString()}
                                    </span>
                                  </td>

                                  <td className="px-4 py-3 text-center">
                                    <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                                      hasOrders ? 'bg-orange-100 text-[#E4572E]' : 'bg-gray-100 text-gray-400'
                                    }`}>
                                      {dayItem.ordersCount}
                                    </span>
                                  </td>

                                  <td className="px-4 py-3 text-right">
                                    {hasOrders ? (
                                      <button
                                        onClick={() => toggleDayExpansion(dayItem.day)}
                                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded bg-gray-100 hover:bg-gray-200 text-gray-700 transition"
                                      >
                                        <span>{isExpanded ? 'Hide' : 'View'}</span>
                                        <span className="text-[10px]">{isExpanded ? '▲' : '▼'}</span>
                                      </button>
                                    ) : (
                                      <span className="text-xs text-gray-400">—</span>
                                    )}
                                  </td>
                                </tr>

                                {/* Expanded Day Orders Drawer */}
                                {isExpanded && hasOrders && (
                                  <tr className="bg-amber-50/20">
                                    <td colSpan={7} className="p-4">
                                      <div className="bg-white rounded-lg border border-amber-200 p-4 shadow-inner">
                                        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                                          <h4 className="text-xs font-bold text-gray-800 uppercase tracking-wider flex items-center gap-1.5">
                                            <span>📦</span> Purchases on {dayItem.date} ({dayItem.orders.length} Orders)
                                          </h4>
                                          <div className="flex items-center gap-3 text-xs font-semibold flex-wrap">
                                            <span className="text-amber-600">⚡ Power: ₹{dayItem.powerLegAmount.toLocaleString()}</span>
                                            <span className="text-teal-600">🌱 Rest: ₹{dayItem.restOfLegAmount.toLocaleString()}</span>
                                            <span className="text-gray-900">Total: ₹{dayItem.totalAmount.toLocaleString()}</span>
                                          </div>
                                        </div>

                                        <div className="overflow-x-auto">
                                          <table className="w-full text-xs">
                                            <thead className="bg-gray-50 text-gray-600 font-semibold border-b border-gray-200">
                                              <tr>
                                                <th className="px-3 py-2 text-left">Order #</th>
                                                <th className="px-3 py-2 text-left">Buyer Name</th>
                                                <th className="px-3 py-2 text-left">Sponsor Code</th>
                                                <th className="px-3 py-2 text-left">Leg Classification</th>
                                                <th className="px-3 py-2 text-left">Direct Leg Leader</th>
                                                <th className="px-3 py-2 text-left">Status</th>
                                                <th className="px-3 py-2 text-right">Purchase Amount</th>
                                              </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100">
                                              {dayItem.orders.map((ord, idx) => {
                                                let legBadge = 'bg-gray-100 text-gray-700';
                                                if (ord.legType === 'POWER_LEG') {
                                                  legBadge = 'bg-amber-100 text-amber-800 border border-amber-300 font-bold';
                                                } else if (ord.legType === 'REST_OF_LEG') {
                                                  legBadge = 'bg-teal-100 text-teal-800 border border-teal-300 font-semibold';
                                                } else if (ord.legType === 'SELF') {
                                                  legBadge = 'bg-emerald-100 text-emerald-800 border border-emerald-300 font-semibold';
                                                }

                                                return (
                                                  <tr key={ord.orderId || idx} className="hover:bg-gray-50/80">
                                                    <td className="px-3 py-2 font-mono text-gray-700">{ord.orderNumber}</td>
                                                    <td className="px-3 py-2 font-medium text-gray-900">{ord.buyerName}</td>
                                                    <td className="px-3 py-2 font-mono text-blue-600">{ord.buyerSponsorCode}</td>
                                                    <td className="px-3 py-2">
                                                      <span className={`px-2 py-0.5 rounded text-[10px] ${legBadge}`}>
                                                        {ord.legType === 'POWER_LEG' ? '⚡ POWER LEG' : ord.legType === 'REST_OF_LEG' ? '🌱 REST OF LEG' : '👤 SELF'}
                                                      </span>
                                                    </td>
                                                    <td className="px-3 py-2 text-gray-600">
                                                      {ord.legLeaderName} {ord.legLeaderCode !== 'N/A' && `(${ord.legLeaderCode})`}
                                                    </td>
                                                    <td className="px-3 py-2">
                                                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${getStatusStyle(ord.status)}`}>
                                                        {ord.status}
                                                      </span>
                                                    </td>
                                                    <td className="px-3 py-2 text-right font-bold text-gray-900">
                                                      ₹{ord.amount.toLocaleString()}
                                                    </td>
                                                  </tr>
                                                );
                                              })}
                                            </tbody>
                                          </table>
                                        </div>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* 📦 ALL CUSTOMER PURCHASES (ADMIN ORDERS MANAGEMENT)       */}
      {/* ======================================================== */}
      {(activeSectionView === 'all' || activeSectionView === 'allPurchases') && (
        <div className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden">
          {/* Header */}
          <div className="p-5 md:p-6 border-b border-gray-200 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold text-[#0F2F3D]">
                Customer Purchase Records ({filteredAllPurchases.length})
              </h2>
              <p className="text-xs text-gray-500 mt-1">
                Manage, confirm, and verify customer orders and payments
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {/* Search */}
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search order #, user, email..."
                  value={adminOrderSearch}
                  onChange={(e) => setAdminOrderSearch(e.target.value)}
                  className="pl-8 pr-3 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E4572E] w-56"
                />
                <span className="absolute left-2.5 top-2 text-gray-400 text-xs">🔍</span>
                {adminOrderSearch && (
                  <button
                    onClick={() => setAdminOrderSearch('')}
                    className="absolute right-2.5 top-1.5 text-gray-400 hover:text-gray-600 text-xs"
                  >
                    ✕
                  </button>
                )}
              </div>

              {/* Status filter */}
              <select
                value={adminOrderStatus}
                onChange={(e) => setAdminOrderStatus(e.target.value)}
                className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E4572E] bg-white font-medium"
              >
                <option value="ALL">All Statuses</option>
                <option value="PENDING">Pending Orders</option>
                <option value="CONFIRMED">Confirmed Orders</option>
                <option value="CANCELLED">Cancelled Orders</option>
              </select>

              <button
                onClick={fetchPurchases}
                className="px-4 py-1.5 bg-gradient-to-r from-[#E4572E] to-[#F47C4D] text-white rounded-lg shadow-sm text-xs font-semibold hover:opacity-90 transition"
              >
                Refresh
              </button>
            </div>
          </div>

          {/* Table Card */}
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center h-48">
                <div className="animate-spin rounded-full h-8 w-8 border-4 border-[#E4572E] border-t-transparent"></div>
              </div>
            ) : (
              <table className="w-full text-left text-xs md:text-sm">
                <thead className="bg-[#0F2F3D] text-white sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-3 font-semibold">SR. No</th>
                    <th className="px-4 py-3 font-semibold">Order #</th>
                    <th className="px-4 py-3 font-semibold">User</th>
                    <th className="px-4 py-3 font-semibold">Email / Code</th>
                    <th className="px-4 py-3 font-semibold">Total Amount</th>
                    <th className="px-4 py-3 font-semibold">Payment</th>
                    <th className="px-4 py-3 font-semibold">Status</th>
                    <th className="px-4 py-3 font-semibold">Actions</th>
                    <th className="px-4 py-3 font-semibold">Date</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 bg-white">
                  {filteredAllPurchases.length > 0 ? (
                    filteredAllPurchases.map((purchase, index) => (
                      <tr
                        key={purchase._id || index}
                        className="hover:bg-[#E4572E]/5 transition-all"
                      >
                        <td className="px-4 py-3 font-medium text-[#0F2F3D]">{index + 1}</td>
                        <td className="px-4 py-3 font-mono font-semibold text-[#0F2F3D]">
                          {purchase.orderNumber}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {purchase.userId?.name || 'N/A'}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500">
                          <div>{purchase.userId?.email || 'N/A'}</div>
                          {purchase.userId?.mySponsorCode && (
                            <div className="font-mono text-blue-600 font-semibold">{purchase.userId.mySponsorCode}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 font-bold text-[#E4572E]">
                          ₹{purchase.totalAmount?.toLocaleString() || 0}
                        </td>
                        <td className="px-4 py-3 text-xs capitalize">
                          {purchase.paymentMethod}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${getStatusStyle(
                              purchase.status
                            )}`}
                          >
                            {purchase.status}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            {purchase.status === 'PENDING' && (
                              <>
                                <button
                                  onClick={() => handleAdminConfirmPurchase(purchase._id)}
                                  disabled={actionLoading === purchase._id}
                                  className="px-2.5 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                                >
                                  {actionLoading === purchase._id ? 'Confirming...' : 'Confirm'}
                                </button>
                                <button
                                  onClick={() => handleAdminCancelPurchase(purchase._id)}
                                  disabled={actionLoading === purchase._id}
                                  className="px-2.5 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                                >
                                  {actionLoading === purchase._id ? 'Cancelling...' : 'Cancel'}
                                </button>
                              </>
                            )}
                            {purchase.status === 'CONFIRMED' && (
                              <span className="text-xs text-green-600 font-medium">✓ Commission Distributed</span>
                            )}
                            {purchase.status === 'CANCELLED' && (
                              <span className="text-xs text-red-600 font-medium">Cancelled</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500">
                          {new Date(purchase.createdAt).toLocaleDateString()}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="9"
                        className="text-center py-10 text-gray-500"
                      >
                        No purchase records found matching your filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPurchases;